import { appOrigin } from '../../lib/hosts.ts';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { api } from '../../lib/api.ts';
import { useGridQuery, useGridState } from '../../lib/grid.ts';
import { LevelBadge, ymd, type PublicNotice } from '../../lib/notices.tsx';
import { dt } from '../../lib/platform.ts';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { PageHeader } from '../../components/ui/PageHeader.tsx';
import { PageActions } from '../../components/ui/PageActions.tsx';
import { buttonClass, LinkButton } from '../../components/ui/Button.tsx';
import { useTitle } from '../../site/site.ts';

/** 전체공지 공개 목록 (CR-030): 인증 없이. 게시 중인 대상 없는 플랫폼 공지만. 공용 서버사이드 데이터테이블. 서비스 사이트 레이아웃 안 (CR-031) */
export function PublicNoticesPage() {
  useTitle('공지사항');
  const { state, set } = useGridState('n', { sort: { col: 'createdAt', dir: 'desc' }, pageSize: 20 });
  const data = useGridQuery<PublicNotice>('public-notices', '/public/notices/grid', state);
  const columns: Column<PublicNotice>[] = [
    { key: 'level', label: '수준', width: '5rem', filter: 'select', render: (n) => <LevelBadge level={n.level} /> },
    { key: 'title', label: '제목', filter: 'text', render: (n) => <Link className="font-semibold text-slate-800 hover:text-accent-strong hover:underline" to={`/notices/${n.id}`}>{n.title}</Link> },
    { key: 'createdAt', label: '등록일', width: '7rem', filter: 'date', render: (n) => ymd(n.createdAt) },
  ];
  return (
    <div className="site-wrap py-8">
      <PageHeader title="공지사항" description="PRISM 서비스 전체 공지입니다. 게시 기간 중인 공지만 보이며, 조직별 공지는 로그인 후 화면 상단에서 확인합니다." />
      {data.isError && <div className="text-red-600 text-sm mb-2">{(data.error as Error).message}</div>}
      <DataTable id="public-notices" columns={columns} data={data.data} state={state} onChange={set} isFetching={data.isFetching} emptyText="게시 중인 공지가 없습니다" />
      <PageActions><a href={`${appOrigin()}/login`} className={buttonClass('secondary')}>로그인</a></PageActions>
    </div>
  );
}

/** 전체공지 공개 상세 (CR-030). 게시가 끝났거나 대상이 있는 공지는 404 */
export function PublicNoticePage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['public-notice', id], queryFn: () => api<PublicNotice>(`/public/notices/${id}`), retry: false });
  if (q.isLoading) return <div className="site-wrap py-8">불러오는 중…</div>;
  if (q.isError || !q.data) return <div className="site-wrap py-8"><PageHeader back={{ to: '/notices', label: '공지사항' }} title="공지를 찾을 수 없습니다" description="게시가 끝났거나 없는 공지입니다." /></div>;
  const n = q.data;
  return (
    <div className="site-wrap py-8">
      <PageHeader back={{ to: '/notices', label: '공지사항' }} title={n.title}>
        <LevelBadge level={n.level} />
        <span className="text-[12px] text-slate-500">등록 {ymd(n.createdAt)} · 게시 {dt(n.startsAt)} ~ {dt(n.endsAt)}</span>
      </PageHeader>
      <div className="min-h-[8rem] border border-slate-200 bg-white p-3 text-[13px] leading-relaxed whitespace-pre-wrap">{n.body || <span className="text-slate-400">본문 없음</span>}</div>
      <PageActions><LinkButton variant="secondary" to="/notices">목록</LinkButton></PageActions>
    </div>
  );
}
